import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { UpdateAgentFormData } from '../../../services/agent/schema/UpdateAgentSchema';
import { AgentData } from '../../../services/agent/response/IndexAgentResponse';
import { IconCheck, IconX } from '@tabler/icons-react';
import clsx from 'clsx';
import { useUpdateAgent } from '../../../services/agent/hooks/useUpdateAgent';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

interface FieldColorComponentProps {
    agent: AgentData;
}

const FieldColorComponent: React.FC<FieldColorComponentProps> = ({ agent }) => {
    const { register, handleSubmit, formState: { errors }, setValue, reset, watch, setError } = useForm<UpdateAgentFormData>({});
    const [isChanged, setIsChanged] = useState(false);

    useEffect(() => {
        if (agent) {
            reset({
                name: agent.name,
                color: agent.color,
                website: agent.website ?? undefined,
                email: agent.email ?? undefined,
                phone: agent.phone ?? undefined,
            });
            setIsChanged(false);
        }
    }, [agent]);

    const { mutate, isPending } = useUpdateAgent();

    const queryClient = useQueryClient();

    const onSubmit = useCallback((data: UpdateAgentFormData) => {
        toast.loading('Updating agent...', { id: 'update-agent' });
        mutate({
            agentId: agent.id,
            formData: data
        }, {
            onSuccess: async () => {

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['agents'].includes(key as string))
                });

                toast.success('Agent updated successfully', { id: 'update-agent' });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdateAgentFormData, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update agent', { id: 'update-agent' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to update agent', { id: 'update-agent' });
                }
            }
        });
    }, [mutate]);

    const currentColor = watch('color');

    useEffect(() => {
        setIsChanged(currentColor !== agent.color);
    }, [currentColor, agent.color]);

    const onReset = () => {
        setValue('color', agent.color);
        setIsChanged(false);
    };

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)} className="d-flex align-items-center color-input-group">
                {/* Color Picker */}
                <input
                    type="color"
                    {...register('color')}
                    className="form-control form-control-color color-picker"
                    onChange={(e) => setValue("color", e.target.value)}
                />
                {/* Text Input for Color */}
                <input
                    type="text"
                    {...register('color')}
                    className={clsx("form-control ms-2 color-text", errors?.color && 'is-invalid')}
                    onChange={(e) => setValue("color", e.target.value)}
                />

                {/* Button muncul jika ada perubahan */}
                {isChanged && (
                    <div className="d-flex ms-2">
                        <button type="submit" className="btn btn-success btn-sm btn-icon me-2" disabled={isPending}>
                            <IconCheck size={14} />
                        </button>
                        <button type="button" onClick={onReset} className="btn btn-danger btn-sm btn-icon" disabled={isPending}>
                            <IconX size={14} />
                        </button>
                    </div>
                )}

                {errors?.color && <div className="invalid-feedback ms-2">{errors.color.message}</div>}
            </form>
        </div>
    );
};

export default FieldColorComponent;
